import { api } from './client';
import type { UserProfile } from '../features/profile/types';
import {
  defaultEffectivePermissions,
  hasAtLeast,
  hasDashboardAccess as hasDashboardAccessPerm,
} from '../features/permissions/lib';
import type { FeatureKey, PermissionLevel } from '../features/permissions/types';

export type { UserProfile } from '../features/profile/types';

export async function getProfile(): Promise<UserProfile | null> {
  try {
    return await api.get<UserProfile>('/api/profile/me');
  } catch {
    return null;
  }
}

/** Completes profile by updating name/phone (profile already exists after register) */
export async function createProfile(data: {
  name: string;
  email: string;
  phone?: string;
}): Promise<UserProfile> {
  return api.patch<UserProfile>('/api/profile/me', {
    name: data.name,
    phone: data.phone ?? '',
  });
}

export async function updateProfile(data: {
  name?: string;
  phone?: string;
}): Promise<UserProfile> {
  return api.patch<UserProfile>('/api/profile/me', data);
}

export async function getAllProfiles(): Promise<UserProfile[]> {
  return api.get<UserProfile[]>('/api/profile/users');
}

export async function updateUserProfile(
  userId: string,
  data: { name?: string; phone?: string; roleId?: string }
): Promise<UserProfile> {
  return api.patch<UserProfile>(`/api/profile/users/${userId}`, data);
}

export async function softDeleteUser(userId: string): Promise<void> {
  await api.delete(`/api/profile/users/${userId}`);
}

/** System ADMIN only — sets password for any user; revokes their sessions */
export async function adminSetUserPassword(userId: string, password: string): Promise<void> {
  await api.patch(`/api/profile/users/${userId}/password`, { password });
}

function effective(profile: UserProfile | null) {
  return profile?.effectivePermissions ?? defaultEffectivePermissions();
}

/** Built-in system administrator (Role.name ADMIN) */
export function isSystemAdmin(profile: UserProfile | null): boolean {
  return profile?.roleName === 'ADMIN';
}

/**
 * Permission check against effectivePermissions. Matches backend: system ADMIN role always has full access.
 * (If effectivePermissions is missing on the client, non-admin users default to none — but ADMIN must still pass.)
 */
export function can(
  profile: UserProfile | null,
  feature: FeatureKey,
  min: PermissionLevel
): boolean {
  if (!profile) return false;
  if (isSystemAdmin(profile)) return true;
  return hasAtLeast(effective(profile)[feature], min);
}

export function hasDashboardAccess(profile: UserProfile | null): boolean {
  if (isSystemAdmin(profile)) return true;
  return hasDashboardAccessPerm(effective(profile));
}

/** @deprecated Prefer can() with a feature key */
export const ROLE_DISPLAY_ADMIN = 'Admin';

/** Committee page / legacy naming */
export function hasCommitteeAccess(profile: UserProfile | null): boolean {
  return can(profile, 'committee', 'view');
}

export function canManageProjects(profile: UserProfile | null): boolean {
  return can(profile, 'projects', 'view');
}

/** Housekeeping / settings: system admin only (permissions UI, full control) */
export function isAdmin(profile: UserProfile | null): boolean {
  return isSystemAdmin(profile);
}

export function showHousekeepingMenu(profile: UserProfile | null): boolean {
  if (!profile) return false;
  if (isSystemAdmin(profile)) return true;
  return (
    can(profile, 'users', 'view') ||
    can(profile, 'roles', 'view') ||
    can(profile, 'notices', 'view') ||
    can(profile, 'classes', 'view') ||
    can(profile, 'contactSettings', 'view') ||
    can(profile, 'gallery', 'view') ||
    can(profile, 'reviews', 'view')
  );
}

export function getHighestRole(profile: UserProfile | null): string {
  return profile?.role ?? 'User';
}

/** @deprecated Use role / ROLE_DISPLAY_ADMIN; kept for compatibility */
export const USER_ROLES = {
  ADMIN: 'ADMIN',
  Admin: ROLE_DISPLAY_ADMIN,
} as const;
